import { html, css, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';

import '../typography/fhi-body/fhi-body.component';

export const FhiRadioSelector = 'fhi-radio';

/**
 * ## FHI Radio
 *
 * {@link https://designsystem.fhi.no/?path=/docs/komponenter-radio--docs}
 *
 * The `<fhi-radio>` component represents a single radio button.
 * It should be used within a group of options, allowing users to select one option from a set.
 *
 * @tag fhi-radio
 * @element fhi-radio
 */
@customElement(FhiRadioSelector)
export class FhiRadio extends LitElement {
  static readonly formAssociated = true;

  /**
   * The text label assigned to and displayed next to the radio.
   * You should always provide a label.
   * @type {string}
   */
  @property({ type: String }) label?: string = undefined;

  /**
   * The name of the radio. This is submitted with the form data as a `key` when the checkbox is checked.
   * All `<fhi-radio>` components with the same name belong to the same group.
   * @reflect
   * @type {string}
   */
  @property({ type: String, reflect: true }) name?: string = undefined;

  /**
   * Sets the visual status of the radio. There is currently only one status available: `error`.
   * The `error` status is used to indicate that there is an issue with the radio, such as a required radio not being checked.
   * @reflect
   * @type {'error' | undefined}
   */
  @property({ type: String, reflect: true }) status?: 'error' = undefined;

  /**
   * Disables the radio. This changes its appearance and makes it non-interactive.
   * @reflect
   * @type {boolean}
   */
  @property({ type: Boolean, reflect: true }) disabled? = false;

  /**
   * Whether the radio button is checked or not.
   * @type {boolean}
   */
  @property({ type: Boolean }) checked? = false;

  /**
   * The value of the radio. This is submitted with the form data as a `value` when the radio is checked.
   * @type {string}
   */
  @property({ type: String }) value: string = 'on';

  /**
   * The help text shown below the label.
   * This is used to provide additional information or descriptions to the user.
   * @type {string}
   */
  @property({ type: String, attribute: 'help-text' }) helpText?: string;

  @query('#input-element')
  private _input!: HTMLInputElement;

  private _internals: ElementInternals;

  /**
   * Indicates if the radio button is part of a form.
   * @internal
   */
  public get isFormElement(): boolean {
    return !!this._internals.form;
  }

  private get _groupRoot(): Document | HTMLFormElement {
    return this._internals.form ?? document;
  }

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  public connectedCallback(): void {
    super.connectedCallback();

    this.addEventListener('focus', this._setFocusOnInput);

    this._updateFormValue();

    // If the radio has a name then it is part of a group and needs keyboard navigation
    if (this.name) {
      this.addEventListener('keydown', this._handleKeyDown);
      this._SetTabbableRadios();
    }
  }

  public disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this._handleKeyDown);
    this.removeEventListener('focus', this._setFocusOnInput);
  }

  private _setFocusOnInput() {
    this._input.focus();
  }

  private _getRadioGroup() {
    return Array.from(
      this._groupRoot.querySelectorAll<FhiRadio>(
        `${FhiRadioSelector}[name="${this.name}"]`,
      ),
    );
  }

  private _SetTabbableRadios() {
    const radios = this._getRadioGroup();

    radios.forEach(radio => {
      // Only the checked radio should be tabbable
      radio.tabIndex = radio.checked ? 0 : -1;
    });

    // If none of the radios are checked, make the first one tabbable
    if (!radios.some(radio => radio.checked) && radios.length > 0) {
      radios[0].tabIndex = 0;
    }
  }

  protected willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('value')) {
      this.value = this.value ?? 'on';
    }
  }

  public updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has('checked')) {
      this._updateFormValue();

      if (this.checked) {
        this.uncheckGroupMembers();
      }
    }

    // update the form value when the value or name input changes and the radio is already checked
    if (changedProperties.has('value') || changedProperties.has('name')) {
      if (this._input.checked) {
        this._updateFormValue();
      }
    }

    if (changedProperties.has('helpText') || changedProperties.has('label')) {
      if (!this.label && this.helpText) {
        console.error(
          'The "help-text" property is set, but the "label" property is not. It is required to provide a label when using help text.',
        );
      }
    }
  }

  public formResetCallback(): void {
    const radiosCheckedByDefault = this._getRadioGroup().filter(
      radio => typeof radio.getAttribute('checked') === 'string',
    );

    this.checked = radiosCheckedByDefault.pop() === this;

    this._updateFormValue();
  }

  private uncheckGroupMembers(): void {
    const radios = this._getRadioGroup();

    radios.forEach(radio => {
      if (radio === this) {
        return;
      }

      if (radio.isFormElement && this._groupRoot === document) {
        return;
      }

      radio.checked = false;
    });
  }

  private _updateFormValue(): void {
    if (!this.isFormElement) {
      return;
    }

    this._internals.setFormValue(this.checked ? this.value : null);
  }

  private _handleChange(event: Event): void {
    this.checked = (event.target as HTMLInputElement).checked;

    this.uncheckGroupMembers();

    this._updateFormValue();

    event.stopPropagation();
    this._dispatchChangeEvent();
  }
  private _handleInput(event: Event): void {
    event.stopPropagation();
    this._dispatchInputEvent();
  }

  private _dispatchChangeEvent(): void {
    /**
     * @type {Event} - Standard DOM event with the type `change`.
     * This event is dispatched when the radio is checked or unchecked.
     */
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private _dispatchInputEvent(): void {
    /**
     * @type {Event} - Standard DOM event with the type `input`.
     * This event is dispatched when the radio is checked or unchecked.
     */
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  }

  private _handleKeyDown(event: KeyboardEvent): void {
    const arrows = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

    if (!arrows.includes(event.key)) {
      return;
    }

    event.preventDefault();

    const radios = this._getRadioGroup();

    if (radios.length < 2) {
      return;
    }

    const currentIndex = radios.indexOf(this);

    const nextIndex = currentIndex;
    let nextRadio: FhiRadio;

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextRadio = radios[nextIndex === 0 ? radios.length - 1 : nextIndex - 1];
    } else {
      nextRadio = radios[nextIndex === radios.length - 1 ? 0 : nextIndex + 1];
    }

    nextRadio.focus();
    this.checked = false;
    nextRadio.checked = true;
    this._SetTabbableRadios();
  }

  render() {
    return html`
      <div class="radio-wrapper">
        <input
          type="radio"
          id="input-element"
          name="${ifDefined(this.name)}"
          value="${this.value}"
          .checked=${live(this.checked ?? false)}
          ?disabled=${this.disabled}
          aria-invalid=${ifDefined(
            this.status === 'error' ? 'true' : undefined,
          )}
          @change=${this._handleChange}
          @input=${this._handleInput}
        />
        <svg
          class="radio-dot"
          viewBox="0 0 18 18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle r="6" cx="9" cy="9" />
        </svg>
      </div>
      ${this.label && html`<label for="input-element">${this.label}</label>`}
      ${this.helpText
        ? html`<fhi-body size="small" class="help-text"
            >${this.helpText}</fhi-body
          >`
        : ''}
    `;
  }

  static styles = css`
    :host {
      --fhi-radio-color: currentcolor;
    }

    :host {
      display: grid;
      width: max-content;
      height: fit-content;
      grid-template-columns: auto auto;
      color: var(--fhi-radio-color);
      padding-left: 0.125rem;

      .radio-wrapper {
        display: flex;
        position: relative;
        height: fit-content;
      }

      label {
        font-family: var(--fhi-font-family-default);
        -webkit-font-smoothing: antialiased;
        font-size: var(--fhi-typography-body-medium-font-size);
        font-weight: var(--fhi-typography-body-medium-font-weight);
        line-height: var(--fhi-typography-body-medium-line-height);
        letter-spacing: var(--fhi-typography-body-medium-letter-spacing);
      }

      label,
      fhi-body {
        grid-column: 2;
        padding-left: 6px;
      }

      input {
        margin: 0.125rem 0 0.125rem 0;
        appearance: none;
        width: var(--fhi-spacing-250);
        height: var(--fhi-spacing-250);
        background-color: var(--fhi-color-neutral-background-default);
        border: var(--fhi-dimension-border-width) solid
          var(--fhi-color-neutral-border-default);
        border-radius: var(--fhi-border-radius-full);
        transition: all var(--fhi-motion-ease-default)
          var(--fhi-motion-duration-quick);
        &:not(:focus-visible) {
          outline-color: var(--fhi-color-accent-surface-hover);
        }
      }

      .radio-dot {
        visibility: hidden;
        opacity: 0;
        transition: opacity var(--fhi-motion-ease-default)
          var(--fhi-motion-duration-quick);
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 1.125rem;
      }

      input:checked {
        border-width: var(--fhi-dimension-border-width-active);
        ~ .radio-dot {
          circle {
            fill: var(--fhi-color-neutral-border-default);
          }
          visibility: visible;
          opacity: 1;
        }
      }

      .help-text {
        color: var(--fhi-color-neutral-text-subtle);
      }
    }

    :host([disabled]) {
      opacity: var(--fhi-opacity-disabled);
      * {
        cursor: not-allowed;
      }
    }

    :host(:not(:disabled)) {
      input:hover:not(:checked) {
        border-color: var(--fhi-color-accent-border-strong);
        background-color: var(--fhi-color-accent-background-subtle);
      }

      input:active:not(:checked) {
        border-color: var(--fhi-color-accent-border-strong);
        background-color: var(--fhi-color-accent-surface-default);
        outline: var(--fhi-dimension-border-width-focus) solid
          var(--fhi-color-accent-surface-hover);
      }

      &:host([status='error']) {
        input {
          border-color: var(--fhi-color-danger-border-default);
          background-color: var(--fhi-color-danger-background-default);
          &:not(:focus-visible) {
            outline-color: var(--fhi-color-danger-surface-hover);
          }
        }

        input:checked {
          ~ .radio-dot {
            circle {
              fill: var(--fhi-color-danger-border-default);
            }
          }
        }

        input:hover:not(:checked) {
          border-color: var(--fhi-color-danger-border-strong);
          background-color: var(--fhi-color-danger-background-subtle);
        }

        input:active:not(:checked) {
          border-color: var(--fhi-color-danger-border-strong);
          background-color: var(--fhi-color-danger-surface-default);
          outline-color: var(--fhi-color-danger-surface-hover);
        }
      }
    }
  `;
}
